import type { ApolloClient } from "@apollo/client";

import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import {
  AbortScoutTurnDocument,
  GetScoutConversationHistoryDocument,
  type ScoutConversationMessage,
  type ScoutHost as ScoutHostGql,
  SendScoutMessageDocument,
} from "generated-graphql/graphql";

import type { StreamCache } from "../../../../stream/cache";
import type { ScoutActiveTurn } from "./types";

import { StreamObserver } from "../../../../stream/observer";
import {
  scoutConversationStore,
  type ScoutConversationSnapshot,
  type ScoutConversationStore,
} from "./scoutConversationStore";

// Builds the SSE URL for a given (tenantId, conversationId). Pluggable so the
// staff observer can target /events/staff/scout/<id> while customer code keeps
// using /events/<tenantId>/scout/<id>. Both URLs ultimately subscribe to the
// same Valkey stream key (scout:conversation:<id>); only the API auth boundary
// differs.
export type ScoutStreamUrlBuilder = (input: {
  tenantId: string | undefined;
  conversationId: string | undefined;
}) => string;

// Loads persisted conversation history. Pluggable so the staff observer can
// hit getScoutConversationHistoryForStaff instead of the customer
// getConversationHistory, while reusing the controller's streaming and
// event-merging machinery. Result shape matches ScoutConversationHistory.
export interface ScoutConversationHistoryLoadInput {
  tenantId: string | undefined;
  conversationId: string;
  apolloClient: ApolloClient<object>;
  forceNetwork: boolean;
  // Initial-page size for the customer surface; the staff observer loader
  // ignores this since it always hydrates the full conversation. Older
  // pages are fetched separately via the controller's beforeSeq path.
  limit?: number;
}
export interface ScoutConversationHistoryResult {
  conversationId: string;
  title?: string | null;
  messages: ScoutConversationMessage[];
}
export type ScoutConversationHistoryLoader = (
  input: ScoutConversationHistoryLoadInput
) => Promise<ScoutConversationHistoryResult | undefined>;

type ControllerOptions = {
  apolloClient: ApolloClient<object>;
  streamClient: StreamCache;
  store?: ScoutConversationStore;
  getIdTokenRefreshIfExpired: () => Promise<string>;
  buildStreamUrl?: ScoutStreamUrlBuilder;
  loadHistory?: ScoutConversationHistoryLoader;
};

type AttachConversationInput = {
  tenantId: string | undefined;
  conversationId: string | undefined;
};

type SendMessageInput = {
  tenantId: string;
  conversationId: string | undefined;
  content: string;
  allowMutations?: boolean;
  pageContext?: string;
  /** Which UI surface initiated the send. Threaded to backend PostHog events. */
  host?: ScoutHostGql;
  attachmentIds?: string[];
  pendingAttachments?: Array<{
    uploadKey: string;
    filename: string;
    mediaType: string;
    sizeBytes: number;
  }>;
  attachmentPreviews?: Array<{
    privateDocumentId: string;
    filename: string;
    mediaType: string;
    sizeBytes: number;
    signedDownloadUrl?: string;
    thumbnailSignedUrl?: string;
    pageCount?: number | null;
    ingestionMode?: string;
    extractionStatus?: string;
    filePartUrl?: string;
  }>;
  extractionWatchPrivateDocumentIds?: string[];
};

type AbortTurnInput = {
  tenantId: string;
  conversationId: string;
};

type RefetchConversationInput = {
  tenantId: string;
  conversationId: string;
};

type LoadHistoryReason =
  | "attach-initial"
  | "abort-recovery"
  | "manual-refetch"
  | "terminal-refetch"
  | "closed-active-refetch";

type EnsureStreamReason =
  | "attach-active-turn"
  | "send-message"
  | "abort-turn"
  | "load-history-active-turn"
  | "history-resume";

type ConversationRuntimeSnapshot = {
  loading: boolean;
  error: Error | undefined;
  isSending: boolean;
  isAborting: boolean;
  isLoadingOlderMessages: boolean;
  hasOlderMessages: boolean;
};

// Controller-owned runtime state for one conversation. This is intentionally
// separate from ScoutConversationStore: the store holds renderable chat data,
// while the controller holds ephemeral transport/query bookkeeping.
type ConversationRuntime = ConversationRuntimeSnapshot & {
  tenantId: string;
  // Number of mounted hook consumers currently interested in this conversation.
  attachmentCount: number;
  // Monotonic guard so stale history responses cannot overwrite newer loads.
  historyLoadVersion: number;
  observer: StreamObserver | undefined;
  observerUnsubscribe: (() => void) | undefined;
  // Cursor used when attaching or re-attaching the SSE stream.
  initialLastEventId: string | undefined;
  // Prevents duplicate application of replayed SSE events after reconnects.
  processedEventIds: Set<string>;
  // Cursor derived from persisted history for "resume from latest known event".
  historyResumeEventId: string | undefined;
  // Prevents repeated final-history refetches for the same terminal event.
  lastTerminalRefetchKey: string | undefined;
  // Prevents repeated recovery refetches when the stream closes multiple times
  // for the same active turn without ever delivering a terminal event.
  lastClosedActiveRefetchKey: string | undefined;
  // Distinguishes a real post-connect close from the observer's bootstrap
  // "closed" snapshot before the network connection has started.
  hasStartedStreamConnection: boolean;
  // Coalesces bursty observer notifications into one controller flush window.
  pendingStreamUpdateTimeout: ReturnType<typeof setTimeout> | undefined;
  olderLoadVersion: number;
  historyNextCursor: string | undefined;
};

export type ScoutConversationControllerSnapshot = {
  storeSnapshot: ScoutConversationSnapshot;
  runtimesByConversationId: Record<string, ConversationRuntimeSnapshot>;
  // When a new conversation starts optimistically, the hook may still be
  // rendering the optimistic id while the controller has already reconciled to
  // the persisted id. This alias map bridges that short handoff window.
  reconciledConversationIdsByConversationId: Record<string, string>;
};

type SendMessageRequest = {
  optimisticConversationId: string;
  completion: Promise<string | undefined>;
};

const EMPTY_CONVERSATION_RUNTIME_SNAPSHOT: ConversationRuntimeSnapshot = {
  loading: false,
  error: undefined,
  isSending: false,
  isAborting: false,
  isLoadingOlderMessages: false,
  hasOlderMessages: false,
};

const EMPTY_CONTROLLER_SNAPSHOT: ScoutConversationControllerSnapshot = {
  storeSnapshot: {
    conversationsById: {},
    messagesByConversationId: {},
    activeTurnsByConversationId: {},
    streamStatusByConversationId: {},
  },
  runtimesByConversationId: {},
  reconciledConversationIdsByConversationId: {},
};

const SCOUT_HISTORY_PAGE_SIZE = 20;

function getTimestampMs(value: unknown): number | undefined {
  if (typeof value !== "string" && typeof value !== "number" && !(value instanceof Date)) {
    return undefined;
  }

  const timestampMs = new Date(value).getTime();
  return Number.isFinite(timestampMs) ? timestampMs : undefined;
}

// Valkey/Redis stream entry ids are formatted as `<ms>-<seq>`. Higher ms wins;
// ties break on seq. Anything malformed is treated as the lowest possible id.
export function compareStreamEntryIds(left: string, right: string): number {
  const [leftMsStr = "", leftSeqStr = ""] = left.split("-");
  const [rightMsStr = "", rightSeqStr = ""] = right.split("-");
  const parseToFinite = (raw: string): number => {
    const value = Number.parseInt(raw, 10);
    return Number.isFinite(value) ? value : 0;
  };
  const leftMs = parseToFinite(leftMsStr);
  const rightMs = parseToFinite(rightMsStr);
  if (leftMs !== rightMs) {
    return leftMs - rightMs;
  }
  return parseToFinite(leftSeqStr) - parseToFinite(rightSeqStr);
}

function readOptionalLastStreamEntryId(message: ScoutConversationMessage): string | undefined {
  if (!("lastStreamEntryId" in message)) {
    return undefined;
  }

  const value = message.lastStreamEntryId;
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// Resume the SSE stream from the most authoritative cursor we have for the
// conversation. The backend stamps `lastStreamEntryId` on every flushed message
// with the Valkey entry id of the latest event consumed, so resuming "after"
// that id replays only events the server hasn't persisted yet — no duplicates,
// no missed deltas. We fall back to a timestamp-derived cursor only for legacy
// messages persisted before this field existed; we use a high seq suffix
// (`-9999999`) to bias the fallback past all entries within the same ms.
export function getHistoryResumeEventId(messages: ScoutConversationMessage[]): string | undefined {
  let bestEntryId: string | undefined;
  let fallbackTimestampMs: number | undefined;

  for (const message of messages) {
    const entryId = readOptionalLastStreamEntryId(message);
    if (entryId) {
      if (bestEntryId === undefined || compareStreamEntryIds(entryId, bestEntryId) > 0) {
        bestEntryId = entryId;
      }
      continue;
    }

    const messageTimestampMs =
      getTimestampMs(message.updatedAt) ?? getTimestampMs(message.createdAt);
    const partTimestampMs = message.parts.reduce<number | undefined>(
      (latestPartTimestampMs, part) => {
        const timestamp = getTimestampMs(part.createdAt);
        if (timestamp === undefined) {
          return latestPartTimestampMs;
        }
        return latestPartTimestampMs === undefined
          ? timestamp
          : Math.max(latestPartTimestampMs, timestamp);
      },
      undefined
    );
    const messageMs =
      messageTimestampMs === undefined && partTimestampMs === undefined
        ? undefined
        : Math.max(messageTimestampMs ?? 0, partTimestampMs ?? 0);
    if (messageMs !== undefined) {
      fallbackTimestampMs =
        fallbackTimestampMs === undefined ? messageMs : Math.max(fallbackTimestampMs, messageMs);
    }
  }

  if (bestEntryId !== undefined) {
    return bestEntryId;
  }
  return fallbackTimestampMs === undefined ? undefined : `${fallbackTimestampMs}-9999999`;
}

// Decides whether to (re)attach the SSE stream after a GraphQL history fetch.
//
// The role-of-last-message rule alone ("resume only if last is user") used to
// be a safe proxy for "the turn is still in flight," because assistant messages
// only landed in Postgres at turn-end. That's no longer true: the materializer
// flushes parts at every part boundary (text-end, tool-result, …), so during
// an active turn the history almost always already contains a partial
// assistant message. Trusting `lastMessage.role` alone now misses the live
// stream and the user sees a frozen half-reply after a refresh.
//
// `hasFreshActiveTurnCursor` is the second signal: if sessionStorage carries a
// recent cursor, the previous tab was actively streaming and we should
// reattach so the live `finish`/`abort`/`error` event from Valkey can settle
// the UI. If the turn is genuinely over server-side, the stream replays the
// terminal event and we tear down — safe and idempotent.
function shouldResumeHistoryStream(
  messages: ScoutConversationMessage[],
  hasFreshActiveTurnCursor: boolean,
  activeTurnRequestId: string | undefined
) {
  const lastMessage = messages.at(-1);
  if (!lastMessage) {
    return true;
  }
  if (
    hasRelevantPersistedAbortMessage(messages, activeTurnRequestId) ||
    hasRelevantPersistedTerminalErrorMessage(messages, activeTurnRequestId)
  ) {
    return false;
  }
  if (lastMessage.role === "user") {
    return true;
  }
  return hasFreshActiveTurnCursor;
}

// In-flight turn metadata stashed in sessionStorage so a browser refresh during
// streaming still resumes from the right Valkey entry id even before the
// GraphQL history fetch comes back. Cleared on any terminal stream event.
const ACTIVE_TURN_STORAGE_PREFIX = "scout:active-turn:";
const ACTIVE_TURN_STORAGE_TTL_MS = 10 * 60 * 1000;

type StoredActiveTurnCursor = {
  requestId: string;
  lastEventId: string;
  updatedAt: number;
};

const TERMINAL_ERROR_PART_ID_PREFIX = "terminal-error-";

function isPersistedAbortMessage(message: ScoutConversationMessage): boolean {
  return message.parts.some((part) => part.type === "abort");
}

function isPersistedTerminalErrorPart(part: ScoutConversationMessage["parts"][number]): boolean {
  const runtimePartId = part.streamPartId ?? part.id;
  return (
    part.type === "text" &&
    part.agentState === "error" &&
    runtimePartId.startsWith(TERMINAL_ERROR_PART_ID_PREFIX)
  );
}

function isPersistedTerminalErrorMessage(message: ScoutConversationMessage): boolean {
  return message.parts.some((part) => isPersistedTerminalErrorPart(part));
}

function isMessageForActiveTurn(
  message: ScoutConversationMessage,
  activeTurnRequestId: string | undefined
): boolean {
  return activeTurnRequestId !== undefined && message.requestId === activeTurnRequestId;
}

function hasRelevantPersistedAbortMessage(
  messages: ScoutConversationMessage[],
  activeTurnRequestId: string | undefined
): boolean {
  const lastMessage = messages.at(-1);
  if (lastMessage && isPersistedAbortMessage(lastMessage)) {
    return true;
  }

  return messages.some((message) => {
    return isMessageForActiveTurn(message, activeTurnRequestId) && isPersistedAbortMessage(message);
  });
}

function hasRelevantPersistedTerminalErrorMessage(
  messages: ScoutConversationMessage[],
  activeTurnRequestId: string | undefined
): boolean {
  const lastMessage = messages.at(-1);
  if (lastMessage && isPersistedTerminalErrorMessage(lastMessage)) {
    return true;
  }

  return messages.some(
    (message) =>
      isMessageForActiveTurn(message, activeTurnRequestId) &&
      isPersistedTerminalErrorMessage(message)
  );
}

function resolveHistoryTerminalStatus(
  messages: ScoutConversationMessage[],
  activeTurnRequestId: string | undefined
): Extract<ScoutActiveTurn["status"], "finished" | "aborted" | "error"> {
  if (hasRelevantPersistedTerminalErrorMessage(messages, activeTurnRequestId)) {
    return "error";
  }

  if (hasRelevantPersistedAbortMessage(messages, activeTurnRequestId)) {
    return "aborted";
  }

  return "finished";
}

function getStoredActiveTurnStorage(): Storage | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    // localStorage rather than sessionStorage: a hard reload that goes through
    // any origin-equivalence-crossing redirect (auth token refresh, login
    // callback, etc.) wipes sessionStorage even within the same tab. The TTL
    // below (`ACTIVE_TURN_STORAGE_TTL_MS`) bounds how stale a stored cursor
    // can be, so per-origin persistence doesn't cross over into "always
    // reattach forever" territory. Multi-tab interaction is tolerable here:
    // every tab that opens this conversation should reattach to the live
    // stream anyway, and the server's terminal events settle the UI.
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function readStoredActiveTurnCursor(conversationId: string): StoredActiveTurnCursor | undefined {
  const storage = getStoredActiveTurnStorage();
  if (!storage) {
    return undefined;
  }
  const raw = storage.getItem(`${ACTIVE_TURN_STORAGE_PREFIX}${conversationId}`);
  if (!raw) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredActiveTurnCursor>;
    if (
      typeof parsed.requestId !== "string" ||
      typeof parsed.lastEventId !== "string" ||
      typeof parsed.updatedAt !== "number"
    ) {
      return undefined;
    }
    if (Date.now() - parsed.updatedAt > ACTIVE_TURN_STORAGE_TTL_MS) {
      storage.removeItem(`${ACTIVE_TURN_STORAGE_PREFIX}${conversationId}`);
      return undefined;
    }
    return {
      requestId: parsed.requestId,
      lastEventId: parsed.lastEventId,
      updatedAt: parsed.updatedAt,
    };
  } catch {
    return undefined;
  }
}

function writeStoredActiveTurnCursor(conversationId: string, cursor: StoredActiveTurnCursor): void {
  const storage = getStoredActiveTurnStorage();
  if (!storage) {
    return;
  }
  try {
    storage.setItem(`${ACTIVE_TURN_STORAGE_PREFIX}${conversationId}`, JSON.stringify(cursor));
  } catch {
    // Quota or private-mode failures are non-fatal — resilience is a best-effort
    // optimization, not a correctness requirement.
  }
}

function clearStoredActiveTurnCursor(conversationId: string): void {
  const storage = getStoredActiveTurnStorage();
  if (!storage) {
    return;
  }
  try {
    storage.removeItem(`${ACTIVE_TURN_STORAGE_PREFIX}${conversationId}`);
  } catch {
    // ignore
  }
}

// Picks the highest of multiple `<ms>-<seq>` cursors, ignoring undefineds.
function maxStreamEntryId(...cursors: Array<string | undefined>): string | undefined {
  let best: string | undefined;
  for (const cursor of cursors) {
    if (!cursor) {
      continue;
    }
    if (best === undefined || compareStreamEntryIds(cursor, best) > 0) {
      best = cursor;
    }
  }
  return best;
}

function isTerminalTurn(turn: ScoutActiveTurn | undefined) {
  return turn?.status === "finished" || turn?.status === "aborted" || turn?.status === "error";
}

function isLocallyActiveTurn(turn: ScoutActiveTurn | undefined): turn is ScoutActiveTurn {
  return turn?.status === "starting" || turn?.status === "streaming";
}

/**
 * Keeps the current assistant message marked as streaming when the local turn is
 * still active and history indicates the SSE stream should resume. Flaky SSE
 * connections can briefly drop before reconnecting, so history ingestion should
 * not mark the assistant message as no longer streaming for a transient stream
 * failure. Terminal history or a non-resumable stream still clears streaming.
 */
function shouldKeepActiveAssistantStreaming(
  activeTurn: ScoutActiveTurn | undefined,
  shouldResumeStream: boolean
) {
  return isLocallyActiveTurn(activeTurn) && shouldResumeStream;
}

function normalizeError(error: unknown): Error | undefined {
  if (!error) {
    return undefined;
  }

  return error instanceof Error ? error : new Error(String(error));
}

// Inline message used when the GraphQL sendMessage mutation itself rejects
// (validation, auth, network, server crash). The runtime cost-limit gate
// surfaces its own customer-facing copy via the SSE stream, so it never
// reaches this catch path — anything thrown here is by definition
// "couldn't even start" and isn't safe to display verbatim to the customer.
const COULD_NOT_START_FALLBACK_MESSAGE = "Scout could not start this conversation.";

export function defaultScoutStreamUrlBuilder({
  tenantId,
  conversationId,
}: {
  tenantId: string | undefined;
  conversationId: string | undefined;
}) {
  if (!tenantId || !conversationId) {
    return "/events/scout/disabled";
  }

  return `/events/${tenantId}/scout/${conversationId}`;
}

export const defaultScoutConversationHistoryLoader: ScoutConversationHistoryLoader = async ({
  tenantId,
  conversationId,
  apolloClient,
  forceNetwork,
  limit,
}) => {
  if (!tenantId) {
    return undefined;
  }

  const result = await apolloClient.query({
    query: GetScoutConversationHistoryDocument,
    variables: {
      tenantId,
      conversationId,
      ...(limit !== undefined ? { limit } : {}),
    },
    fetchPolicy: forceNetwork ? "network-only" : "cache-first",
  });

  return result.data?.getConversationHistory ?? undefined;
};

function createOptimisticId(kind: string) {
  return `optimistic-scout-${kind}-${crypto.randomUUID()}`;
}

function createScoutRequestId() {
  return crypto.randomUUID();
}

function isOptimisticId(id: string | undefined) {
  return id?.startsWith("optimistic-scout-") ?? false;
}

function shouldDebugScoutConversation() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.localStorage.getItem("scout:debug") === "1" ||
    new URLSearchParams(window.location.search).get("scoutDebug") === "1"
  );
}

function debugScoutConversation(label: string, details: Record<string, unknown>) {
  if (!shouldDebugScoutConversation()) {
    return;
  }

  console.debug(`[ScoutConversation] ${label}`, details);
}

function hasHydratedConversationMessages(
  snapshot: ScoutConversationSnapshot,
  conversationId: string
) {
  return conversationId in snapshot.messagesByConversationId;
}

/**
 * Owns Scout's non-React runtime behavior.
 *
 * Responsibilities:
 * - load/refetch conversation history
 * - orchestrate optimistic send/abort flows
 * - attach SSE observers and apply live events into the store
 * - keep per-conversation transport/query bookkeeping out of React state
 *
 * Non-responsibilities:
 * - render-facing chat state; that lives in ScoutConversationStore
 * - route/display selection; the hook still decides which conversation the UI
 *   is currently showing
 */
export class ScoutConversationController {
  private readonly apolloClient: ApolloClient<object>;
  private readonly streamClient: StreamCache;
  private readonly store: ScoutConversationStore;
  private readonly getIdTokenRefreshIfExpired: () => Promise<string>;
  private readonly buildStreamUrl: ScoutStreamUrlBuilder;
  private readonly historyLoader: ScoutConversationHistoryLoader;
  private readonly listeners = new Set<() => void>();
  private readonly runtimeByConversationId = new Map<string, ConversationRuntime>();
  private snapshot: ScoutConversationControllerSnapshot;

  public constructor(options: ControllerOptions) {
    this.apolloClient = options.apolloClient;
    this.streamClient = options.streamClient;
    this.store = options.store ?? scoutConversationStore;
    this.getIdTokenRefreshIfExpired = options.getIdTokenRefreshIfExpired;
    this.buildStreamUrl = options.buildStreamUrl ?? defaultScoutStreamUrlBuilder;
    this.historyLoader = options.loadHistory ?? defaultScoutConversationHistoryLoader;
    this.snapshot = {
      ...EMPTY_CONTROLLER_SNAPSHOT,
      storeSnapshot: this.store.getSnapshot(),
    };
    this.store.subscribe(this.handleStoreUpdate);
  }

  public subscribe = (listener: () => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  public getSnapshot = () => this.snapshot;

  // Registers interest in a conversation. Multiple mounted consumers can attach
  // to the same conversation without fighting over a singleton runtime.
  public attachConversation(input: AttachConversationInput) {
    if (!input.tenantId || !input.conversationId) {
      return;
    }

    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    runtime.attachmentCount += 1;
    const snapshot = this.store.getSnapshot();
    const activeTurn = snapshot.activeTurnsByConversationId[input.conversationId];

    debugScoutConversation("attach conversation", {
      conversationId: input.conversationId,
      attachmentCount: runtime.attachmentCount,
      activeTurnStatus: activeTurn?.status,
      hasObserver: Boolean(runtime.observer),
      hasHydratedMessages: hasHydratedConversationMessages(snapshot, input.conversationId),
    });

    if (isOptimisticId(input.conversationId)) {
      return;
    }

    if (isLocallyActiveTurn(activeTurn)) {
      const storedCursor = readStoredActiveTurnCursor(input.conversationId);
      this.ensureStreamAttached({
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        initialLastEventId: maxStreamEntryId(
          activeTurn.lastEventId,
          runtime.historyResumeEventId,
          storedCursor?.lastEventId
        ),
        reason: "attach-active-turn",
      });
      return;
    }

    if (
      runtime.attachmentCount === 1 &&
      !runtime.loading &&
      !hasHydratedConversationMessages(snapshot, input.conversationId)
    ) {
      void this.loadHistory({
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        forceNetwork: false,
        reason: "attach-initial",
      });
    }
  }

  // Releases one consumer's interest in a conversation. The runtime stays alive
  // if another consumer is attached or if a turn is still actively streaming.
  public detachConversation(conversationId: string | undefined) {
    if (!conversationId) {
      return;
    }

    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime) {
      return;
    }

    runtime.attachmentCount = Math.max(0, runtime.attachmentCount - 1);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[conversationId];
    if (runtime.attachmentCount === 0 && !isLocallyActiveTurn(activeTurn)) {
      this.detachStream(conversationId);
      this.maybeDeleteRuntime(conversationId);
    }
  }

  public detachAll() {
    const conversationIds = [...this.runtimeByConversationId.keys()];
    for (const conversationId of conversationIds) {
      this.detachStream(conversationId);
      this.runtimeByConversationId.delete(conversationId);
    }

    this.setControllerSnapshot({
      ...EMPTY_CONTROLLER_SNAPSHOT,
      storeSnapshot: this.store.getSnapshot(),
    });
  }

  // Starts a new turn for the given conversation (or a new optimistic
  // conversation) and returns both the optimistic id and a promise for the
  // reconciled/persisted conversation id.
  public sendMessage(input: SendMessageInput): SendMessageRequest {
    const trimmedContent = input.content.trim();
    const hasAttachments =
      (input.attachmentIds?.length ?? 0) > 0 ||
      (input.attachmentPreviews?.length ?? 0) > 0 ||
      (input.extractionWatchPrivateDocumentIds?.length ?? 0) > 0 ||
      (input.pendingAttachments?.length ?? 0) > 0;
    if (trimmedContent.length === 0 && !hasAttachments) {
      return {
        optimisticConversationId: input.conversationId ?? createOptimisticId("conversation"),
        completion: Promise.resolve(undefined),
      };
    }

    let targetConversationId = input.conversationId;
    if (targetConversationId !== undefined && isOptimisticId(targetConversationId)) {
      targetConversationId =
        this.snapshot.reconciledConversationIdsByConversationId[targetConversationId];
    }
    const optimisticConversationId =
      targetConversationId ?? input.conversationId ?? createOptimisticId("conversation");
    const optimisticUserMessageId = createOptimisticId("message");
    const optimisticRequestId = createScoutRequestId();
    const runtime = this.ensureRuntime(optimisticConversationId, input.tenantId);
    const activeTurn = targetConversationId
      ? this.store.getSnapshot().activeTurnsByConversationId[targetConversationId]
      : undefined;
    const targetRuntime = targetConversationId
      ? this.runtimeByConversationId.get(targetConversationId)
      : undefined;
    const historyResumeEventId = targetRuntime?.historyResumeEventId;
    const targetStreamInitialLastEventId = targetConversationId
      ? (activeTurn?.lastEventId ?? historyResumeEventId)
      : undefined;

    this.setRuntimeSnapshot(optimisticConversationId, {
      isSending: true,
      isAborting: false,
      loading: false,
      error: undefined,
    });
    this.store.startOptimisticTurn({
      tenantId: input.tenantId,
      conversationId: optimisticConversationId,
      userMessageId: optimisticUserMessageId,
      requestId: optimisticRequestId,
      content: trimmedContent,
      attachments: input.attachmentPreviews,
    });

    const attachmentIds = [
      ...new Set([
        ...(input.attachmentIds ?? []),
        ...(input.attachmentPreviews?.map((attachment) => attachment.privateDocumentId) ?? []),
      ]),
    ];

    const completion = this.apolloClient
      .mutate({
        mutation: SendScoutMessageDocument,
        variables: {
          input: {
            tenantId: input.tenantId,
            requestId: optimisticRequestId,
            conversationId: targetConversationId,
            content: trimmedContent,
            ...(input.pendingAttachments && input.pendingAttachments.length > 0
              ? { pendingAttachments: input.pendingAttachments }
              : {}),
            extractionWatchPrivateDocumentIds: input.extractionWatchPrivateDocumentIds,
            allowMutations: input.allowMutations ?? false,
            ...(input.allowMutations === true ? { allowMutations: true } : {}),
            ...(input.pageContext ? { pageContext: input.pageContext } : {}),
            ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
            ...(input.host ? { host: input.host } : {}),
          },
        },
      })
      .then((result) => {
        const sendResult = result.data?.sendMessage;
        if (!sendResult) {
          throw new Error("Scout did not return a send result.");
        }

        this.store.reconcileOptimisticTurn({
          tenantId: input.tenantId,
          optimisticConversationId,
          conversationId: sendResult.conversationId,
          optimisticUserMessageId,
          userMessageId: sendResult.messageId,
          optimisticRequestId,
          requestId: sendResult.requestId,
          content: trimmedContent,
          title: sendResult.title,
        });

        if (optimisticConversationId !== sendResult.conversationId) {
          this.transferRuntime({
            fromConversationId: optimisticConversationId,
            toConversationId: sendResult.conversationId,
            tenantId: input.tenantId,
          });
        }

        this.setRuntimeSnapshot(sendResult.conversationId, {
          isSending: false,
          error: undefined,
        });
        this.ensureStreamAttached({
          tenantId: input.tenantId,
          conversationId: sendResult.conversationId,
          initialLastEventId: targetStreamInitialLastEventId,
          reason: "send-message",
        });
        this.maybeDeleteRuntime(optimisticConversationId);

        return sendResult.conversationId;
      })
      .catch((error) => {
        this.store.markTurnError({
          conversationId: optimisticConversationId,
          requestId: optimisticRequestId,
          error: COULD_NOT_START_FALLBACK_MESSAGE,
          couldNotStart: true,
        });
        this.setRuntimeSnapshot(optimisticConversationId, {
          isSending: false,
          error: normalizeError(error),
        });
        throw error;
      });

    return {
      optimisticConversationId,
      completion,
    };
  }

  // Aborts the active turn for one conversation and keeps listening long enough
  // to observe the terminal abort event from Scout.
  public async abortTurn(input: AbortTurnInput): Promise<boolean> {
    if (isOptimisticId(input.conversationId)) {
      return false;
    }

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];

    this.ensureRuntime(input.conversationId, input.tenantId);
    this.setRuntimeSnapshot(input.conversationId, {
      isAborting: true,
      error: undefined,
    });

    try {
      const result = await this.apolloClient.mutate({
        mutation: AbortScoutTurnDocument,
        variables: {
          input: {
            tenantId: input.tenantId,
            conversationId: input.conversationId,
          },
        },
      });
      const abortResult = result.data?.abortTurn;
      if (!abortResult) {
        this.setRuntimeSnapshot(input.conversationId, {
          isAborting: false,
        });
        return false;
      }

      if (abortResult.success) {
        this.store.markTurnPending({
          conversationId: abortResult.conversationId,
          requestId: abortResult.requestId,
        });
        this.ensureStreamAttached({
          tenantId: input.tenantId,
          conversationId: abortResult.conversationId,
          initialLastEventId: activeTurn?.lastEventId,
          reason: "abort-turn",
        });
        void this.loadHistory({
          tenantId: input.tenantId,
          conversationId: abortResult.conversationId,
          forceNetwork: true,
          allowWhileActive: true,
          reason: "abort-recovery",
        });
      }

      this.setRuntimeSnapshot(input.conversationId, {
        isAborting: false,
      });

      return abortResult.success;
    } catch (error) {
      this.setRuntimeSnapshot(input.conversationId, {
        isAborting: false,
        error: normalizeError(error),
      });
      throw error;
    }
  }

  // Forces a persisted history refresh for one conversation.
  public async refetchConversation(input: RefetchConversationInput): Promise<void> {
    if (isOptimisticId(input.conversationId)) {
      return;
    }

    await this.loadHistory({
      tenantId: input.tenantId,
      conversationId: input.conversationId,
      forceNetwork: true,
      reason: "manual-refetch",
    });
  }

  // Loads one older history page to support upward infinite scroll.
  public async loadOlderMessages(input: RefetchConversationInput): Promise<void> {
    if (isOptimisticId(input.conversationId)) {
      return;
    }

    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    if (runtime.isLoadingOlderMessages || !runtime.hasOlderMessages) {
      return;
    }

    const beforeSeqRaw = runtime.historyNextCursor;
    const beforeSeq = beforeSeqRaw === undefined ? undefined : safeParseInt(beforeSeqRaw);
    if (
      runtime.hasOlderMessages &&
      (beforeSeqRaw === undefined ||
        beforeSeq === undefined ||
        beforeSeq === null ||
        beforeSeq <= 0)
    ) {
      return;
    }

    runtime.olderLoadVersion += 1;
    const loadVersion = runtime.olderLoadVersion;
    this.setRuntimeSnapshot(input.conversationId, {
      isLoadingOlderMessages: true,
      error: undefined,
    });

    try {
      const result = await this.apolloClient.query({
        query: GetScoutConversationHistoryDocument,
        variables: {
          tenantId: input.tenantId,
          conversationId: input.conversationId,
          limit: SCOUT_HISTORY_PAGE_SIZE,
          beforeSeq,
        },
        fetchPolicy: "network-only",
      });
      const latestRuntime = this.runtimeByConversationId.get(input.conversationId);
      if (!latestRuntime || latestRuntime.olderLoadVersion !== loadVersion) {
        return;
      }

      const history = result.data?.getConversationHistory;
      if (history) {
        this.handleOlderHistoryResult({
          tenantId: input.tenantId,
          conversationId: input.conversationId,
          history,
        });
      }

      this.setRuntimeSnapshot(input.conversationId, {
        isLoadingOlderMessages: false,
      });
    } catch (error) {
      const latestRuntime = this.runtimeByConversationId.get(input.conversationId);
      if (!latestRuntime || latestRuntime.olderLoadVersion !== loadVersion) {
        return;
      }

      this.setRuntimeSnapshot(input.conversationId, {
        isLoadingOlderMessages: false,
        error: normalizeError(error),
      });
    }
  }

  private ensureRuntime(conversationId: string, tenantId: string): ConversationRuntime {
    const existing = this.runtimeByConversationId.get(conversationId);
    if (existing) {
      if (existing.tenantId !== tenantId) {
        existing.tenantId = tenantId;
      }
      return existing;
    }

    const runtime: ConversationRuntime = {
      ...EMPTY_CONVERSATION_RUNTIME_SNAPSHOT,
      tenantId,
      attachmentCount: 0,
      historyLoadVersion: 0,
      observer: undefined,
      observerUnsubscribe: undefined,
      initialLastEventId: undefined,
      processedEventIds: new Set<string>(),
      historyResumeEventId: undefined,
      lastTerminalRefetchKey: undefined,
      lastClosedActiveRefetchKey: undefined,
      hasStartedStreamConnection: false,
      pendingStreamUpdateTimeout: undefined,
      olderLoadVersion: 0,
      historyNextCursor: undefined,
    };
    this.runtimeByConversationId.set(conversationId, runtime);
    this.setControllerSnapshot({
      runtimesByConversationId: {
        ...this.snapshot.runtimesByConversationId,
        [conversationId]: EMPTY_CONVERSATION_RUNTIME_SNAPSHOT,
      },
    });
    return runtime;
  }

  // Mirrors controller-local runtime flags into the external-store snapshot that
  // hooks subscribe to.
  private setRuntimeSnapshot(
    conversationId: string,
    nextFields: Partial<ConversationRuntimeSnapshot>
  ) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime) {
      return;
    }

    const nextSnapshot = {
      loading: "loading" in nextFields ? (nextFields.loading ?? false) : runtime.loading,
      error: "error" in nextFields ? nextFields.error : runtime.error,
      isSending: "isSending" in nextFields ? (nextFields.isSending ?? false) : runtime.isSending,
      isAborting:
        "isAborting" in nextFields ? (nextFields.isAborting ?? false) : runtime.isAborting,
      isLoadingOlderMessages:
        "isLoadingOlderMessages" in nextFields
          ? (nextFields.isLoadingOlderMessages ?? false)
          : runtime.isLoadingOlderMessages,
      hasOlderMessages:
        "hasOlderMessages" in nextFields
          ? (nextFields.hasOlderMessages ?? false)
          : runtime.hasOlderMessages,
    };
    const didChange =
      runtime.loading !== nextSnapshot.loading ||
      runtime.error !== nextSnapshot.error ||
      runtime.isSending !== nextSnapshot.isSending ||
      runtime.isAborting !== nextSnapshot.isAborting ||
      runtime.isLoadingOlderMessages !== nextSnapshot.isLoadingOlderMessages ||
      runtime.hasOlderMessages !== nextSnapshot.hasOlderMessages;

    runtime.loading = nextSnapshot.loading;
    runtime.error = nextSnapshot.error;
    runtime.isSending = nextSnapshot.isSending;
    runtime.isAborting = nextSnapshot.isAborting;
    runtime.isLoadingOlderMessages = nextSnapshot.isLoadingOlderMessages;
    runtime.hasOlderMessages = nextSnapshot.hasOlderMessages;

    if (!didChange) {
      return;
    }

    this.setControllerSnapshot({
      runtimesByConversationId: {
        ...this.snapshot.runtimesByConversationId,
        [conversationId]: nextSnapshot,
      },
    });
  }

  private deleteRuntime(conversationId: string, options?: { preserveAlias?: boolean }) {
    const didDelete = this.runtimeByConversationId.delete(conversationId);
    if (!didDelete) {
      return;
    }

    const { [conversationId]: _deleted, ...remainingRuntimes } =
      this.snapshot.runtimesByConversationId;
    this.setControllerSnapshot({
      runtimesByConversationId: remainingRuntimes,
      reconciledConversationIdsByConversationId: options?.preserveAlias
        ? this.snapshot.reconciledConversationIdsByConversationId
        : Object.fromEntries(
            Object.entries(this.snapshot.reconciledConversationIdsByConversationId).filter(
              ([aliasConversationId]) => aliasConversationId !== conversationId
            )
          ),
    });
  }

  private maybeDeleteRuntime(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime || runtime.attachmentCount > 0 || runtime.observer) {
      return;
    }

    const snapshot = this.store.getSnapshot();
    const hasConversationData =
      snapshot.conversationsById[conversationId] !== undefined ||
      (snapshot.messagesByConversationId[conversationId]?.length ?? 0) > 0;

    if (
      runtime.loading ||
      runtime.isSending ||
      runtime.isAborting ||
      runtime.error ||
      hasConversationData
    ) {
      return;
    }

    this.deleteRuntime(conversationId);
  }

  // Moves controller bookkeeping from an optimistic conversation id to the real
  // persisted id once sendMessage returns from the backend.
  private transferRuntime(input: {
    fromConversationId: string;
    toConversationId: string;
    tenantId: string;
  }) {
    const fromRuntime = this.runtimeByConversationId.get(input.fromConversationId);
    if (!fromRuntime) {
      return;
    }

    const toRuntime = this.ensureRuntime(input.toConversationId, input.tenantId);
    toRuntime.attachmentCount += fromRuntime.attachmentCount;
    toRuntime.loading = fromRuntime.loading || toRuntime.loading;
    toRuntime.error = fromRuntime.error ?? toRuntime.error;
    toRuntime.isSending = fromRuntime.isSending || toRuntime.isSending;
    toRuntime.isAborting = fromRuntime.isAborting || toRuntime.isAborting;
    toRuntime.isLoadingOlderMessages =
      fromRuntime.isLoadingOlderMessages || toRuntime.isLoadingOlderMessages;
    toRuntime.hasOlderMessages = fromRuntime.hasOlderMessages || toRuntime.hasOlderMessages;
    toRuntime.historyResumeEventId =
      fromRuntime.historyResumeEventId ?? toRuntime.historyResumeEventId;
    toRuntime.historyNextCursor = fromRuntime.historyNextCursor ?? toRuntime.historyNextCursor;
    for (const processedEventId of fromRuntime.processedEventIds) {
      toRuntime.processedEventIds.add(processedEventId);
    }

    this.setControllerSnapshot({
      runtimesByConversationId: {
        ...this.snapshot.runtimesByConversationId,
        [input.toConversationId]: {
          loading: toRuntime.loading,
          error: toRuntime.error,
          isSending: toRuntime.isSending,
          isAborting: toRuntime.isAborting,
          isLoadingOlderMessages: toRuntime.isLoadingOlderMessages,
          hasOlderMessages: toRuntime.hasOlderMessages,
        },
      },
      reconciledConversationIdsByConversationId: {
        ...this.snapshot.reconciledConversationIdsByConversationId,
        [input.fromConversationId]: input.toConversationId,
      },
    });

    if (fromRuntime.observer) {
      fromRuntime.observerUnsubscribe?.();
      fromRuntime.observer.destroy();
    }
    this.deleteRuntime(input.fromConversationId, {
      preserveAlias: true,
    });
  }

  private async loadHistory(input: {
    tenantId: string;
    conversationId: string;
    forceNetwork: boolean;
    allowWhileActive?: boolean;
    reason: LoadHistoryReason;
  }) {
    if (isOptimisticId(input.conversationId)) {
      return;
    }

    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    if (!input.allowWhileActive && isLocallyActiveTurn(activeTurn)) {
      debugScoutConversation("skip history load for active turn", {
        conversationId: input.conversationId,
        reason: input.reason,
        activeTurnStatus: activeTurn.status,
        lastEventId: activeTurn.lastEventId,
      });
      this.ensureStreamAttached({
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        initialLastEventId: activeTurn.lastEventId ?? runtime.historyResumeEventId,
        reason: "load-history-active-turn",
      });
      this.setRuntimeSnapshot(input.conversationId, {
        loading: false,
      });
      return;
    }

    runtime.historyLoadVersion += 1;
    const loadVersion = runtime.historyLoadVersion;
    debugScoutConversation("load history", {
      conversationId: input.conversationId,
      reason: input.reason,
      forceNetwork: input.forceNetwork,
      allowWhileActive: Boolean(input.allowWhileActive),
      loadVersion,
    });
    this.setRuntimeSnapshot(input.conversationId, {
      loading: true,
      error: undefined,
    });

    try {
      const history = await this.historyLoader({
        tenantId: input.tenantId,
        conversationId: input.conversationId,
        apolloClient: this.apolloClient,
        forceNetwork: input.forceNetwork,
        limit: SCOUT_HISTORY_PAGE_SIZE,
      });
      const latestRuntime = this.runtimeByConversationId.get(input.conversationId);
      if (!latestRuntime || latestRuntime.historyLoadVersion !== loadVersion) {
        return;
      }

      if (history) {
        this.handleHistoryResult({
          tenantId: input.tenantId,
          conversationId: input.conversationId,
          history,
        });
      }

      this.setRuntimeSnapshot(input.conversationId, {
        loading: false,
      });
    } catch (error) {
      const latestRuntime = this.runtimeByConversationId.get(input.conversationId);
      if (!latestRuntime || latestRuntime.historyLoadVersion !== loadVersion) {
        return;
      }

      this.setRuntimeSnapshot(input.conversationId, {
        loading: false,
        error: normalizeError(error),
      });
    }
  }

  // History is ingested into the store immediately; the controller then decides
  // whether that history implies "stream should still be active".
  private handleHistoryResult(input: {
    tenantId: string;
    conversationId: string;
    history: {
      conversationId: string;
      title?: string | null;
      allowMutations?: boolean;
      messages: ScoutConversationMessage[];
      pageInfo?: {
        hasMore: boolean;
        nextCursor?: string | null;
      };
    };
  }) {
    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    runtime.historyResumeEventId = getHistoryResumeEventId(input.history.messages);

    // Read the stored cursor up front: it both helps decide whether to resume
    // (a fresh cursor means the tab was actively streaming when it reloaded)
    // and contributes to the resume position itself (it can sit beyond the
    // most recent flushed message because we update it on every event).
    const storedCursor = readStoredActiveTurnCursor(input.history.conversationId);
    const hasFreshActiveTurnCursor = storedCursor !== undefined;
    const shouldResumeStream = shouldResumeHistoryStream(
      input.history.messages,
      hasFreshActiveTurnCursor,
      activeTurn?.requestId
    );

    debugScoutConversation("ingest history", {
      conversationId: input.history.conversationId,
      messageCount: input.history.messages.length,
      activeTurnStatus: activeTurn?.status,
      hasObserver: Boolean(runtime.observer),
    });
    this.store.ingestHistory({
      tenantId: input.tenantId,
      conversationId: input.history.conversationId,
      title: input.history.title,
      allowMutations: input.history.allowMutations,
      messages: input.history.messages,
      keepActiveAssistantStreaming: shouldKeepActiveAssistantStreaming(
        activeTurn,
        shouldResumeStream
      ),
    });
    runtime.hasOlderMessages = input.history.pageInfo?.hasMore ?? false;
    runtime.historyNextCursor = input.history.pageInfo?.nextCursor ?? undefined;
    this.setRuntimeSnapshot(input.conversationId, {
      hasOlderMessages: runtime.hasOlderMessages,
      isLoadingOlderMessages: false,
    });

    if (!shouldResumeStream) {
      if (isLocallyActiveTurn(activeTurn)) {
        this.store.markTurnFinishedFromHistory({
          conversationId: input.conversationId,
          requestId: activeTurn.requestId,
          status: resolveHistoryTerminalStatus(input.history.messages, activeTurn.requestId),
          lastEventId: runtime.historyResumeEventId,
        });
      }
      clearStoredActiveTurnCursor(input.conversationId);
      this.detachStream(input.conversationId);
      return;
    }

    if (isLocallyActiveTurn(activeTurn) && runtime.observer) {
      debugScoutConversation("keep active stream after history ingest", {
        conversationId: input.history.conversationId,
        activeTurnStatus: activeTurn.status,
        resumeEventId: runtime.historyResumeEventId,
      });
      return;
    }

    const initialLastEventId = maxStreamEntryId(
      runtime.historyResumeEventId,
      storedCursor?.lastEventId
    );

    debugScoutConversation("resume stream from history", {
      conversationId: input.history.conversationId,
      resumeEventId: initialLastEventId,
      reattachReason: hasFreshActiveTurnCursor ? "fresh-active-turn-cursor" : "user-trailing",
    });
    this.ensureStreamAttached({
      tenantId: input.tenantId,
      conversationId: input.history.conversationId,
      initialLastEventId,
      reason: "history-resume",
    });
  }

  private handleOlderHistoryResult(input: {
    tenantId: string;
    conversationId: string;
    history: {
      conversationId: string;
      title?: string | null;
      allowMutations?: boolean;
      messages: ScoutConversationMessage[];
      pageInfo?: {
        hasMore: boolean;
        nextCursor?: string | null;
      };
    };
  }) {
    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    this.store.prependHistoryPage({
      tenantId: input.tenantId,
      conversationId: input.history.conversationId,
      title: input.history.title,
      allowMutations: input.history.allowMutations,
      messages: input.history.messages,
    });

    runtime.hasOlderMessages = input.history.pageInfo?.hasMore ?? false;
    runtime.historyNextCursor = input.history.pageInfo?.nextCursor ?? undefined;
    this.setRuntimeSnapshot(input.conversationId, {
      hasOlderMessages: runtime.hasOlderMessages,
    });
  }

  // Streams are conversation-scoped, not hook-scoped. This method makes sure the
  // right observer exists for the conversation and resume cursor we want.
  private ensureStreamAttached(input: {
    tenantId: string;
    conversationId: string;
    initialLastEventId: string | undefined;
    reason: EnsureStreamReason;
  }) {
    if (isOptimisticId(input.conversationId)) {
      return;
    }

    const runtime = this.ensureRuntime(input.conversationId, input.tenantId);
    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    if (isTerminalTurn(activeTurn)) {
      this.detachStream(input.conversationId);
      return;
    }

    const streamUrl = this.buildStreamUrl({
      tenantId: input.tenantId,
      conversationId: input.conversationId,
    });
    const currentStreamKey = runtime.observer
      ? `${streamUrl}:${runtime.initialLastEventId ?? "none"}`
      : undefined;
    const nextStreamKey = `${streamUrl}:${input.initialLastEventId ?? "none"}`;

    debugScoutConversation("ensure stream attached", {
      conversationId: input.conversationId,
      reason: input.reason,
      currentStreamKey,
      nextStreamKey,
      activeTurnStatus: activeTurn?.status,
    });

    if (runtime.observer && currentStreamKey === nextStreamKey) {
      this.handleStreamUpdate(input.conversationId);
      return;
    }

    this.detachStream(input.conversationId);
    runtime.initialLastEventId = input.initialLastEventId;
    runtime.hasStartedStreamConnection = false;
    runtime.observer = new StreamObserver(this.streamClient, {
      enabled: true,
      url: streamUrl,
      initialLastEventId: input.initialLastEventId,
      getIdTokenRefreshIfExpired: this.getIdTokenRefreshIfExpired,
    });
    runtime.observerUnsubscribe = runtime.observer.subscribe(() => {
      this.scheduleStreamUpdate(input.conversationId);
    });
    void runtime.observer.connect().catch(() => undefined);
  }

  private detachStream(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime?.observer) {
      return;
    }

    debugScoutConversation("detach stream", {
      conversationId,
      streamKey: `${this.buildStreamUrl({
        tenantId: runtime.tenantId,
        conversationId,
      })}:${runtime.initialLastEventId ?? "none"}`,
    });

    runtime.observerUnsubscribe?.();
    runtime.observerUnsubscribe = undefined;
    runtime.observer.destroy();
    runtime.observer = undefined;
    runtime.initialLastEventId = undefined;
    runtime.hasStartedStreamConnection = false;
    if (runtime.pendingStreamUpdateTimeout !== undefined) {
      clearTimeout(runtime.pendingStreamUpdateTimeout);
      runtime.pendingStreamUpdateTimeout = undefined;
    }
    this.store.setStreamStatus(conversationId, "closed");
  }

  private scheduleStreamUpdate(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime || runtime.pendingStreamUpdateTimeout !== undefined) {
      return;
    }

    runtime.pendingStreamUpdateTimeout = setTimeout(() => {
      const latestRuntime = this.runtimeByConversationId.get(conversationId);
      if (!latestRuntime) {
        return;
      }

      latestRuntime.pendingStreamUpdateTimeout = undefined;
      this.handleStreamUpdate(conversationId);
    }, 0);
  }

  private handleStreamUpdate(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime?.observer) {
      return;
    }

    const streamResult = runtime.observer.getCurrentResult();
    if (streamResult.status === "connecting" || streamResult.status === "connected") {
      runtime.hasStartedStreamConnection = true;
    }
    let sawTerminalEvent = false;
    let latestProcessedEventId: string | undefined;
    this.store.batch(() => {
      this.store.setStreamStatus(conversationId, streamResult.status);

      for (const event of streamResult.data) {
        const processedEventKey = `${conversationId}:${event.id}`;
        if (runtime.processedEventIds.has(processedEventKey)) {
          continue;
        }

        runtime.processedEventIds.add(processedEventKey);
        latestProcessedEventId = event.id;
        debugScoutConversation("stream event", {
          conversationId,
          eventId: event.id,
          eventName: event.event,
          hasPart: Boolean(
            typeof event.data === "object" && event.data !== null && "part" in event.data
          ),
        });
        sawTerminalEvent = this.store.applyStreamEvent(event) || sawTerminalEvent;
      }
    });

    if (streamResult.error) {
      this.setRuntimeSnapshot(conversationId, {
        error: streamResult.error,
      });
    }

    // Linearise the terminal vs. non-terminal cursor handling. Earlier this
    // wrote the in-flight cursor first and then conditionally cleared it on
    // terminal — which worked but depended on the implicit guarantee that the
    // store's `batch` had already flipped `activeTurn` out of "locally active"
    // by the time we read the snapshot for the cursor write. Inverting the
    // order makes intent explicit: terminal events clear; non-terminal events
    // write the latest in-flight cursor.
    if (sawTerminalEvent) {
      debugScoutConversation("terminal stream event", {
        conversationId,
        lastEventId: streamResult.lastEventId,
      });
      clearStoredActiveTurnCursor(conversationId);
      this.detachStream(conversationId);
      void this.maybeRefetchTerminalHistory(conversationId);
      return;
    }

    // Persist the freshest cursor for the active turn — but only if this
    // update actually delivered an event. `streamResult.lastEventId` reflects
    // the cursor we asked the server to resume from (set when we attached the
    // stream), so it's truthy even before any event has arrived. Writing that
    // would let a stream that never delivered anything still mark a turn as
    // "in flight on this tab," and that's exactly the state we use to decide
    // whether to keep retrying after a refresh.
    if (latestProcessedEventId !== undefined) {
      const postEventActiveTurn =
        this.store.getSnapshot().activeTurnsByConversationId[conversationId];
      if (isLocallyActiveTurn(postEventActiveTurn) && postEventActiveTurn.requestId) {
        writeStoredActiveTurnCursor(conversationId, {
          requestId: postEventActiveTurn.requestId,
          lastEventId: latestProcessedEventId,
          updatedAt: Date.now(),
        });
      }
    }

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[conversationId];
    if (streamResult.status === "closed" && isLocallyActiveTurn(activeTurn)) {
      // The underlying Stream owns reconnection (bounded exponential backoff),
      // so a transient `closed` snapshot resolves itself once the next retry
      // succeeds. We only fall back to refetching history through GraphQL,
      // which is a coarser but more reliable recovery path used after the
      // backoff budget is exhausted. `maybeRefetchClosedActiveTurnHistory`
      // debounces per (conversationId, requestId, lastEventId) via
      // `runtime.lastClosedActiveRefetchKey`, so multiple closed-status
      // notifications for the same disconnect window fire the refetch at
      // most once.
      if (!runtime.hasStartedStreamConnection) {
        debugScoutConversation("ignore pre-connect closed stream snapshot", {
          conversationId,
          requestId: activeTurn.requestId,
          lastEventId: streamResult.lastEventId,
        });
        return;
      }

      void this.maybeRefetchClosedActiveTurnHistory({
        conversationId,
        requestId: activeTurn.requestId,
        lastEventId: streamResult.lastEventId,
      });
    }
  }

  private async maybeRefetchTerminalHistory(conversationId: string) {
    const runtime = this.runtimeByConversationId.get(conversationId);
    if (!runtime) {
      return;
    }

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[conversationId];
    if (!activeTurn?.lastEventId || !isTerminalTurn(activeTurn)) {
      return;
    }

    const refetchKey = `${conversationId}:${activeTurn.lastEventId}`;
    if (runtime.lastTerminalRefetchKey === refetchKey) {
      return;
    }

    runtime.lastTerminalRefetchKey = refetchKey;
    debugScoutConversation("refetch terminal history", {
      conversationId,
      lastEventId: activeTurn.lastEventId,
      status: activeTurn.status,
    });

    await this.loadHistory({
      tenantId: runtime.tenantId,
      conversationId,
      forceNetwork: true,
      reason: "terminal-refetch",
    });

    if (runtime.attachmentCount === 0 && !isLocallyActiveTurn(activeTurn)) {
      this.maybeDeleteRuntime(conversationId);
    }
  }

  private async maybeRefetchClosedActiveTurnHistory(input: {
    conversationId: string;
    requestId: string;
    lastEventId: string | undefined;
  }) {
    const runtime = this.runtimeByConversationId.get(input.conversationId);
    if (!runtime) {
      return;
    }

    const refetchKey = `${input.conversationId}:${input.requestId}:${input.lastEventId ?? "none"}`;
    if (runtime.lastClosedActiveRefetchKey === refetchKey) {
      return;
    }

    runtime.lastClosedActiveRefetchKey = refetchKey;
    debugScoutConversation("refetch closed active stream history", {
      conversationId: input.conversationId,
      requestId: input.requestId,
      lastEventId: input.lastEventId,
    });

    await this.loadHistory({
      tenantId: runtime.tenantId,
      conversationId: input.conversationId,
      forceNetwork: true,
      allowWhileActive: true,
      reason: "closed-active-refetch",
    });

    const activeTurn = this.store.getSnapshot().activeTurnsByConversationId[input.conversationId];
    if (runtime.attachmentCount === 0 && !isLocallyActiveTurn(activeTurn)) {
      this.maybeDeleteRuntime(input.conversationId);
    }
  }

  private notifyListeners() {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private handleStoreUpdate = () => {
    const nextStoreSnapshot = this.store.getSnapshot();
    if (this.snapshot.storeSnapshot === nextStoreSnapshot) {
      return;
    }

    this.setControllerSnapshot({
      storeSnapshot: nextStoreSnapshot,
    });
  };

  private setControllerSnapshot(nextFields: Partial<ScoutConversationControllerSnapshot>) {
    this.snapshot = {
      storeSnapshot: nextFields.storeSnapshot ?? this.snapshot.storeSnapshot,
      runtimesByConversationId:
        nextFields.runtimesByConversationId ?? this.snapshot.runtimesByConversationId,
      reconciledConversationIdsByConversationId:
        nextFields.reconciledConversationIdsByConversationId ??
        this.snapshot.reconciledConversationIdsByConversationId,
    };
    this.notifyListeners();
  }
}
